import React from 'react';
import { Star } from 'lucide-react';

/** Portion of star `index` (1–5) filled for an average in [0, 5]. */
export function starFillFraction(average: number, index: number): number {
  const v = Math.min(5, Math.max(0, average));
  return Math.min(1, Math.max(0, v - (index - 1)));
}

const sizeMap = {
  sm: 'h-2.5 w-2.5',
  md: 'h-3.5 w-3.5'
} as const;

export type DecimalRatingStarsSize = keyof typeof sizeMap;

interface DecimalRatingStarsProps {
  average: number;
  size?: DecimalRatingStarsSize;
  className?: string;
}

const OUTLINE_STROKE = 1.75;

/**
 * Five-star display with fractional fill aligned to the numeric average (not integer rounding).
 * Empty stars use a gray outline; partial stars use gray on the empty segment only; filled stars use the same stroke weight as outlines (amber stroke, not gray) so size matches empty stars.
 */
export const DecimalRatingStars: React.FC<DecimalRatingStarsProps> = ({
  average,
  size = 'sm',
  className = ''
}) => {
  const dim = sizeMap[size];
  return (
    <span className={`inline-flex items-center gap-px ${className}`} aria-hidden>
      {[1, 2, 3, 4, 5].map((i) => {
        const frac = starFillFraction(average, i);
        const shell = `relative inline-flex shrink-0 ${dim}`;
        if (frac <= 0) {
          return (
            <span key={i} className={shell}>
              <Star
                className={`pointer-events-none absolute inset-0 ${dim} fill-transparent text-gray-400 dark:text-gray-500`}
                strokeWidth={OUTLINE_STROKE}
              />
            </span>
          );
        }
        if (frac >= 1) {
          return (
            <span key={i} className={shell}>
              <Star
                className={`pointer-events-none absolute inset-0 ${dim} fill-amber-400 text-amber-400 stroke-amber-400`}
                strokeWidth={OUTLINE_STROKE}
              />
            </span>
          );
        }
        return (
          <span key={i} className={shell}>
            <Star
              className={`pointer-events-none absolute inset-0 ${dim} fill-transparent text-gray-400 dark:text-gray-500`}
              strokeWidth={OUTLINE_STROKE}
            />
            <span
              className="pointer-events-none absolute inset-y-0 left-0 overflow-hidden"
              style={{ width: `${frac * 100}%` }}
            >
              <Star
                className={`pointer-events-none absolute inset-0 ${dim} fill-amber-400 text-amber-400 stroke-amber-400`}
                strokeWidth={OUTLINE_STROKE}
              />
            </span>
          </span>
        );
      })}
    </span>
  );
};
